import { useFetch } from "~vue/api";
import { type Goal, type GoalListResponse } from "~vue/types/goals";
import { sortGoalsByDateDesc } from "~vue/utils/goals";
import { computed, type MaybeRefOrGetter, ref, toValue, watch } from "vue";

const DEFAULT_PAGE_SIZE = 10;

export interface UseListGoalsOptions {
    status: MaybeRefOrGetter<"active" | "completed" | null>;
    forTeamMember: MaybeRefOrGetter<boolean | null>;
    source?: MaybeRefOrGetter<"user" | "extracted" | null>;
    pageSize?: number | null;
}

export function useListGoals({ status, forTeamMember, source = null, pageSize = DEFAULT_PAGE_SIZE }: UseListGoalsOptions) {
    const goals = ref<Goal[]>([]);
    const cursor = ref<string | null>(null);
    const hasMore = ref(false);
    const isAppending = ref(false);
    const loadingGoalIds = ref<Set<string>>(new Set());

    const url = computed(() => {
        const params = new URLSearchParams();
        if (pageSize !== null) {
            params.set("page_size", String(pageSize));
        }

        const statusValue = toValue(status);
        if (statusValue !== null) {
            params.set("status", statusValue);
        }

        const forTeamMemberValue = toValue(forTeamMember);
        if (forTeamMemberValue !== null) {
            params.set("for_team_member", String(forTeamMemberValue));
        }

        const sourceValue = toValue(source);
        if (sourceValue !== null) {
            params.set("source", sourceValue);
        }

        if (cursor.value !== null) {
            params.set("cursor", cursor.value);
        }

        return `/v1/goals/?${params.toString()}`;
    });

    const { data, isFetching, execute: _execute } = useFetch(url, { immediate: false }).json<GoalListResponse>();

    watch(data, (page) => {
        if (!page) {
            return;
        }
        const incoming = isAppending.value ? [...goals.value, ...page.goals] : page.goals;
        goals.value = sortGoalsByDateDesc(incoming);
        hasMore.value = page.has_more_goals;
        cursor.value = page.cursor;
        isAppending.value = false;
    });

    async function fetch() {
        isAppending.value = false;
        cursor.value = null;
        await _execute();
    }

    async function loadMore() {
        if (!hasMore.value || isFetching.value) {
            return;
        }
        isAppending.value = true;
        await _execute();
    }

    function removeGoal(goalId: string): void {
        goals.value = goals.value.filter((g) => g.id !== goalId);
    }

    async function refreshGoal(goalOrId: string | Goal): Promise<void> {
        let updated: Goal;
        if (typeof goalOrId === "string") {
            loadingGoalIds.value = new Set([...loadingGoalIds.value, goalOrId]);
            const { data: refreshed } = await useFetch(`/v1/goals/${goalOrId}`, { immediate: true }).json<Goal>();
            loadingGoalIds.value = new Set([...loadingGoalIds.value].filter((id) => id !== goalOrId));
            if (!refreshed.value) {
                return;
            }
            updated = refreshed.value;
        } else {
            updated = goalOrId;
        }
        const idx = goals.value.findIndex((g) => g.id === updated.id);
        if (idx !== -1) {
            const existing = goals.value[idx];
            if (existing.actions && updated.actions) {
                const updatedMap = new Map(updated.actions.map((a) => [a.id, a]));
                const preserved = existing.actions.map((a) => updatedMap.get(a.id)).filter((a): a is NonNullable<typeof a> => a !== undefined);
                const added = updated.actions.filter((a) => !existing.actions!.some((ea) => ea.id === a.id));
                updated = { ...updated, actions: [...preserved, ...added] };
            }
            goals.value = [...goals.value.slice(0, idx), updated, ...goals.value.slice(idx + 1)];
        } else {
            goals.value = sortGoalsByDateDesc([updated, ...goals.value]);
        }
    }

    function goalIsLoading(goalId: string): boolean {
        return loadingGoalIds.value.has(goalId);
    }

    return {
        goals,
        isFetching,
        hasMore,
        fetch,
        loadMore,
        refreshGoal,
        removeGoal,
        goalIsLoading,
    };
}
